/**
 * Pure helpers for sending state: kill switches, pending sends and alarm kinds. No React here, so they
 * are unit-tested (apps/console/test/sending.test.ts).
 */
import { ALARM_HINTS, alarmHint, type Channel, type PendingMessageView, type SwitchScope, type SwitchesView } from '@takeit4me/shared'

const CHANNEL_WORDS: Record<Channel, string> = { email: 'Email', sms: 'SMS', chat: 'Web chat', voice: 'Phone', whatsapp: 'WhatsApp' }
export const SCOPE_LABELS: Record<SwitchScope, string> = { company: 'Everything (the whole company)', ...CHANNEL_WORDS }

export interface PauseItem {
  /** platform and region come from takeit4me and cannot be changed in the console. */
  scope: 'platform' | 'region' | SwitchScope
  title: string
  reason: string | null
  set_by: string | null
  set_at: string | null
  readOnly: boolean
}

/** Everything paused right now, broadest first. Empty when sending is running normally. */
export function pausedItems(sw: SwitchesView | null | undefined): PauseItem[] {
  if (!sw) return []
  const out: PauseItem[] = []
  if (sw.platform.paused) out.push({ scope: 'platform', title: 'All sending is paused by takeit4me', reason: sw.platform.reason, set_by: 'takeit4me', set_at: null, readOnly: true })
  if (sw.region.paused) out.push({ scope: 'region', title: 'Sending in this region is paused by takeit4me', reason: sw.region.reason, set_by: 'takeit4me', set_at: null, readOnly: true })
  if (sw.company.paused) out.push({ scope: 'company', title: 'Sending is paused for the whole company', reason: sw.company.reason, set_by: sw.company.set_by, set_at: sw.company.set_at, readOnly: false })
  for (const ch of Object.keys(CHANNEL_WORDS) as Channel[]) {
    const d = sw.channel_details?.[ch]
    if (d?.paused || sw.channels?.[ch]) {
      out.push({ scope: ch, title: `${CHANNEL_WORDS[ch]} sending is paused`, reason: d?.reason ?? null, set_by: d?.set_by ?? null, set_at: d?.set_at ?? null, readOnly: false })
    }
  }
  return out
}

/** A short line for the app shell: "Sending paused", "SMS paused", "2 pauses". */
export function pauseHeadline(items: PauseItem[]): string | null {
  if (!items.length) return null
  if (items.some((i) => i.scope === 'platform' || i.scope === 'region' || i.scope === 'company')) return 'Sending paused'
  if (items.length === 1) return items[0].title.replace(' sending is paused', ' paused')
  return `${items.length} channels paused`
}

/** "04:59" (or "1:02:03" past an hour) until `at`; "now" once it has passed. */
export function countdown(at: string | null, now: number): string {
  if (!at) return ''
  const s = Math.ceil((new Date(at).getTime() - now) / 1000)
  if (s <= 0) return 'now'
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(sec).padStart(2, '0')
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/** Why a message is waiting: held by a pause, counting down a veto window, or due now (the send job picks it up). */
export function pendingState(p: Pick<PendingMessageView, 'status' | 'send_after' | 'paused'>, now: number): 'paused' | 'veto' | 'due' {
  if (p.paused) return 'paused'
  if (p.status === 'scheduled' && p.send_after && new Date(p.send_after).getTime() > now) return 'veto'
  return 'due'
}

/** A waiting message on a timeline: a scheduled AI reply, or anything queued behind a pause. */
export function isWaitingMessage(m: { status: string; send_after: string | null; policy: { paused?: string | null } | null; direction: string }): boolean {
  if (m.direction !== 'outbound') return false
  return (m.status === 'scheduled' && !!m.send_after) || (m.status === 'queued' && !!m.policy?.paused)
}

export type AlarmIcon = 'silence' | 'send' | 'inbox' | 'ai' | 'clock' | 'pause' | 'desk' | 'budget' | 'alert'
/** `todo`: what a person should do about it, when we know (ALARM_HINTS in @takeit4me/shared). */
export interface AlarmKindInfo { label: string; icon: AlarmIcon; help: string; todo?: string }
const ALARMS: Record<string, AlarmKindInfo> = {
  silence: { label: 'Channel gone quiet', icon: 'silence', help: 'Much less coming in than normal, even though nothing errored.' },
  send_failed: { label: 'Sending failed', icon: 'send', help: 'The provider refused a message on this channel.' },
  send_unconfirmed: { label: 'Send not confirmed', icon: 'send', help: 'A message may or may not have gone; it is on the desk to check.' },
  intake_dead: { label: 'Incoming message stuck', icon: 'inbox', help: 'An incoming message could not be processed after several tries.' },
  ai_failing: { label: 'The AI is failing', icon: 'ai', help: 'The AI could not answer customers; their messages went to the desk.' },
  unanswered: { label: 'Customers waiting', icon: 'clock', help: 'Customer messages with no reply and no hand-over.' },
  switches: { label: 'Sending paused', icon: 'pause', help: 'A kill switch is holding outbound messages.' },
  deadline: { label: 'Deadline missed', icon: 'clock', help: 'A promise or desk item passed its time.' },
  queue_unworked: { label: 'Nobody on the desk', icon: 'desk', help: 'Items are waiting and nobody is on duty.' },
  error_budget: { label: 'Error budget used', icon: 'budget', help: 'Critical errors this week passed the company budget.' },
}
const HINT_ICONS: Record<string, AlarmIcon> = { outbound_failed: 'send', case_paused: 'pause', inbound_unmatched: 'inbox', queue_age: 'clock', journey_stale: 'clock', journey_stalled: 'clock', opt_out_review: 'desk', inbound_reconcile: 'inbox', inbox_reconcile: 'inbox', inbound_media: 'inbox', whatsapp: 'send', sweep_failing: 'alert' }
/**
 * `kind` with the alarm's subject (queue_age + inbound), or a `kind:subject` key (queue_age:email): the per-queue hint
 * when there is one, falling back to the kind's own.
 */
export function alarmKind(key: string, subject?: string | null): AlarmKindInfo {
  const kind = subject !== undefined || ALARM_HINTS[key] || ALARMS[key] ? key : key.split(':')[0]
  const sub = subject ?? (kind !== key ? key.slice(kind.length + 1) : null)
  const hint = alarmHint(kind, sub)
  const own = sub ? ALARM_HINTS[`${kind}:${sub}`] : undefined
  const known = ALARMS[kind]
  if (known) return hint ? { ...known, ...(own ? { label: own.label } : {}), todo: hint.todo } : known
  if (hint) return { label: hint.label, icon: HINT_ICONS[kind] ?? 'alert', help: hint.label, todo: hint.todo }
  return { label: kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()), icon: 'alert', help: '' }
}
/** The channel an alarm is about, from its subject (`channel:<id>`, or `channel-drop:<id>` for "quieter than normal"), if any. */
export function alarmChannelId(subject: string | null | undefined): string | null {
  // Outcome and intake findings add a suffix (`channel:<id>:low_answer_rate`): the channel is the first part.
  return subject?.startsWith('channel:') ? subject.slice(8).split(':')[0] : subject?.startsWith('channel-drop:') ? subject.slice(13).split(':')[0] : null
}
/** An alarm's label: a silence alarm filed as a drop (services/silence.ts) is "quieter than normal", not silent. */
export function alarmLabel(kind: string, subject: string | null | undefined): string {
  return kind === 'silence' && subject?.startsWith('channel-drop:') ? 'Quieter than normal' : alarmKind(kind, subject).label
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/**
 * Where an alarm's subject lives in the console, so a person can act on it in one click (alarm rows act in
 * place): a conversation, a person (`person:<id>` or `person:<id>:<channel>`), a desk item, a message's conversation
 * (the API resolves `conversation_id` for `message:<id>`), unknown senders, journey runs, or a queue's own screen.
 */
export function alarmLink(a: { kind: string; subject?: string | null; conversation_id?: string | null; url?: string | null }): { to: string; label: string } | null {
  // The check named where the finding lives (alarm.detail.url): that wins.
  if (a.url && a.url.startsWith('/') && !a.url.startsWith('//')) {
    return { to: a.url, label: a.url.startsWith('/conversations/') ? 'Open the conversation' : a.url.startsWith('/crm/people/') ? 'Open the customer' : 'Open' }
  }
  const subject = a.subject ?? ''
  const [prefix, id] = subject.split(':')
  const valid = !!id && UUID_RE.test(id)
  if (prefix === 'conversation' && valid) return { to: `/conversations/${id}`, label: 'Open the conversation' }
  if (a.conversation_id) return { to: `/conversations/${a.conversation_id}`, label: 'Open the conversation' }
  if (prefix === 'person' && valid) return { to: `/crm/people/${id}`, label: 'Open the customer' }
  if (prefix === 'exception' && valid) return { to: `/exceptions/${id}`, label: 'Open the desk item' }
  if (a.kind === 'inbound_unmatched') return { to: '/conversations?filter=unmatched', label: 'Open unknown senders' }
  if (a.kind === 'unanswered') return { to: '/conversations?filter=needs_reply', label: 'Open "Needs a reply"' }
  if (a.kind === 'outbound_failed') return { to: '/conversations?filter=failed', label: 'Open "Not delivered"' }
  if (a.kind === 'case_stale') return { to: '/conversations?filter=with_person', label: 'Open "With a person"' }
  if (a.kind === 'opt_out_review') return { to: '/conversations/opt-outs', label: 'Open Opt-outs' }
  if (a.kind === 'queue_age' && (subject === 'sends' || subject === 'sending' || subject === 'email')) return { to: '/conversations/outbound', label: 'Open Outbound' }
  if (a.kind === 'stale_items' || a.kind === 'queue_ignored' || a.kind === 'queue_unworked' || a.kind === 'deadline') return { to: '/exceptions', label: 'Open the desk' }
  if (prefix === 'journey_run' || a.kind === 'journey_stale' || a.kind === 'journeys_waiting_person') return { to: '/studio/journeys', label: 'Open journeys' }
  return null
}
